// src/components/foam/FoamRegister.js
//
// Screen 2 · Register (minimal — retailer selection only)
// Doc: 2026-06 design-system handoff "Screen 2" + SPEC §2 + §4.1
// Updated 2026-06-04: WAKA Design System pass — Saira headline, pinned-footer
// layout (CTA always visible — supersedes the scrollable-list cap from the
// same day's smoke test), square corners, uppercase Archivo button labels.

import React, { useState } from 'react';
import { FScreen, FHeader, FLabel, FHeadline, FBoardCard, FBtn } from './FoamPrimitives';

const RETAILERS = [
  { id: 'waka.surf',  label: 'waka.surf',          sub: 'Official store' },
  { id: 'MACkite',    label: 'MACkite',            sub: 'United States'  },
  // id must match shouldAutoVerifyRetailer's list in shopifyVerificationService.js
  // ('Green Hat Kiteboarding', not 'GreenHat') — fixed 2026-06-04 smoke test.
  { id: 'Green Hat Kiteboarding', label: 'Green Hat', sub: 'United States' },
  { id: 'Windance',   label: 'Windance',           sub: 'United States'  },
  { id: '30Noeuds',   label: '30 Nœuds',           sub: 'Canada / EU'    },
  { id: 'Other',      label: 'Other / second-hand', sub: 'Manual review (1–2 days)' },
];

const FoamRegister = ({
  product,
  matchedRetailer,   // string | null — Shopify match preselects this
  onSubmit,
  submitting = false,
  error = null,
}) => {
  // Default selection: the matched retailer if known, else waka.surf (first row)
  const [selected, setSelected] = useState(matchedRetailer || RETAILERS[0].id);
  const isOrderMatched = matchedRetailer && selected === matchedRetailer;

  const header = (
    <>
      <FHeader right={
        <div className="flex gap-[4px] items-center">
          <span style={{ width: 22, height: 4, background: 'var(--foam-ink)' }} />
          <span style={{ width: 22, height: 4, background: 'var(--foam-ink)' }} />
        </div>
      } />
      <div className="mt-[20px]">
        <FLabel>Step 2 of 2 · Last one</FLabel>
        <FHeadline size={46} lineHeight={0.92} className="mt-[10px]">
          Where did you<br />buy it?
        </FHeadline>
      </div>
    </>
  );

  const footer = (
    <>
      {error && (
        <div className="font-mono text-[12px] tracking-[0.1em] uppercase text-foam-orange-d">
          ● {error}
        </div>
      )}
      <FBtn
        primary
        disabled={submitting}
        onClick={() => onSubmit?.(selected)}
      >
        {submitting ? 'Registering…' : 'Register board →'}
      </FBtn>
      <div className="text-[12px] text-foam-ink-2 leading-[1.5] text-center">
        Findability turns on by default — if it's ever lost, a finder can reach you.
        You stay in control of what they see.
      </div>
    </>
  );

  return (
    <FScreen header={header} footer={footer}>
      <FBoardCard product={product} small />

      {/* Retailer list lives in the scrollable body; the CTA is pinned below
          and always visible (Nigel, 2026-06-04 smoke test). */}
      <div
        className="flex flex-col"
        style={{ border: '1.5px solid var(--foam-line)' }}
      >
        {RETAILERS.map((r, i) => {
          const isSelected = selected === r.id;
          const isLast = i === RETAILERS.length - 1;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelected(r.id)}
              className="text-left p-[16px] flex justify-between items-center"
              style={{
                background: isSelected ? 'var(--foam-orange)' : 'var(--foam-paper)',
                color: isSelected ? 'var(--foam-carbon)' : 'var(--foam-ink)',
                borderBottom: isLast ? 'none' : `1px solid ${isSelected ? 'var(--foam-orange)' : 'var(--foam-hair)'}`,
              }}
            >
              <div>
                <div className="text-[15px] font-semibold">{r.label}</div>
                <div
                  className="text-[12px] mt-[1px]"
                  style={{ color: isSelected ? '#5a1a00' : 'var(--foam-ink-2)' }}
                >
                  {r.sub}
                </div>
              </div>
              {isSelected && isOrderMatched && (
                <div
                  className="font-mono text-[12px] px-[8px] py-[5px] uppercase font-medium"
                  style={{
                    background: 'var(--foam-carbon)',
                    color: 'var(--foam-orange)',
                    letterSpacing: '0.08em',
                  }}
                >
                  order matched
                </div>
              )}
            </button>
          );
        })}
      </div>
    </FScreen>
  );
};

export default FoamRegister;
